import type { Difficulty } from '../bot/types';
import type { HeroId } from '../types';
import { autoLoadout, powerupById, type PowerupId } from '../powerups';
import { addTrickStock, ROAD_TRICK_GRANT } from './trickStock';
import { pushLedger } from './ledger';
import { CRITTER_GATES, ROAD, TROPHY_BANDS } from './tables';
import type { MatchOutcome, Profile, TrophyBand } from './types';

const CLASS_HERO: Record<string, HeroId> = {
  guardian: 'pip',
  infiltration: 'bramble',
  stealth: 'cleo',
  tidecaller: 'otto',
};

/** Grants a road trick at level 1. An opening-kit trick fills an empty disc. */
function grantTrick(profile: Profile, id: string): boolean {
  if (profile.tricks[id]) return false;
  const def = powerupById(id);
  if (!def) return false;
  profile.tricks[id] = { level: 1 };
  addTrickStock(profile, id, ROAD_TRICK_GRANT);
  if (def.classId === 'universal' || def.classId === 'grove') return true;
  const hero = CLASS_HERO[def.classId];
  if (!hero) return true;
  const kit = autoLoadout(hero);
  if (!kit.includes(id as PowerupId)) return true;
  const slots = profile.loadouts[hero];
  if (!slots.includes(id as PowerupId) && slots.length < 2) slots.push(id as PowerupId);
  return true;
}

/** Last band whose minimum is still at or under this best. */
export function bandForBest(best: number): TrophyBand {
  let found: TrophyBand = TROPHY_BANDS[0];
  for (const band of TROPHY_BANDS) {
    if (best >= band.min) found = band;
  }
  return found;
}

export function difficultyForTrophies(best: number): Difficulty {
  return bandForBest(best).bot;
}

export function trophyDelta(best: number, outcome: MatchOutcome): number {
  if (outcome === 'tie') return 0;
  const band = bandForBest(best);
  return outcome === 'win' ? band.win : -band.loss;
}

/** Moves current trophies. The band and the floor both read best trophies from before the match. */
export function applyTrophy(profile: Profile, outcome: MatchOutcome): number {
  const delta = trophyDelta(profile.bestTrophies, outcome);
  const floor = bandForBest(profile.bestTrophies).floor;
  profile.trophies = Math.max(floor, profile.trophies + delta);
  profile.bestTrophies = Math.max(profile.bestTrophies, profile.trophies);
  return delta;
}

/** Unlocks critters the best has reached, and grants any missing opening kit at level 1. */
export function syncCritters(profile: Profile): HeroId[] {
  const granted: HeroId[] = [];
  for (const gate of CRITTER_GATES) {
    if (profile.bestTrophies < gate.at) continue;
    const row = profile.critters[gate.id];
    if (!row) continue;
    const was = row.unlocked;
    row.unlocked = true;
    let kitNew = false;
    const slots = profile.loadouts[gate.id];
    for (const id of autoLoadout(gate.id)) {
      const def = powerupById(id);
      if (!def || def.unlockAt > gate.at) continue;
      if (grantTrick(profile, id)) kitNew = true;
      // Grove never auto-slots, and a shared kit is already owned by the class lead.
      if (profile.tricks[id] && slots && !slots.includes(id) && slots.length < 2) {
        slots.push(id);
        kitNew = true;
      }
    }
    if (!was || kitNew) granted.push(gate.id);
  }
  return granted;
}

export interface RoadGrant {
  acorns: number;
  honey: number;
  critters: HeroId[];
}

/** Claims every reached node once. A second call pays nothing. */
export function claimReached(profile: Profile, now: number): RoadGrant {
  let acorns = 0;
  let honey = 0;
  for (const node of ROAD) {
    if (profile.bestTrophies < node.at) continue;
    if (profile.road.claimed.includes(node.at)) continue;
    profile.road.claimed.push(node.at);
    acorns += node.acorns;
    honey += node.honey;
    if (node.trick) grantTrick(profile, node.trick);
    if (node.acorns || node.honey) {
      const currencies: { acorns?: number; honey?: number } = {};
      if (node.acorns) currencies.acorns = node.acorns;
      if (node.honey) currencies.honey = node.honey;
      pushLedger(profile, now, 'road', currencies, 0, String(node.at));
    }
  }
  if (acorns) profile.wallet.acorns += acorns;
  if (honey) profile.wallet.honey += honey;
  const critters = syncCritters(profile);
  return { acorns, honey, critters };
}
